import type { VerificationStatus } from "../../api/client";

interface VerifyProgressBarProps {
  status: VerificationStatus;
  dealName: string;
  onProceed: () => void;
}

function TrackBar({
  label,
  verified,
  total,
}: {
  label: string;
  verified: number;
  total: number;
}) {
  const pct = total > 0 ? Math.round((verified / total) * 100) : 100;

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-bark-600 truncate">
          {label}
        </span>
        <span className="text-xs tabular-nums text-bark-500 ml-2 whitespace-nowrap">
          {verified} of {total}
        </span>
      </div>
      <div className="h-2 bg-grove-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-forest-600 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function VerifyProgressBar({
  status,
  dealName,
  onProceed,
}: VerifyProgressBarProps) {
  const canProceed = status.can_proceed;

  // Group doc progress by file_type
  const rrDocs = status.documents.filter((d) => d.file_type === "rent_roll");
  const osDocs = status.documents.filter((d) => d.file_type === "operating_statement");
  const camDocs = status.documents.filter((d) => d.file_type === "cam_rec");

  const sumTrack = (docs: typeof status.documents) => ({
    verified: docs.reduce((s, d) => s + d.verified_fields, 0),
    total: docs.reduce((s, d) => s + d.total_fields, 0),
  });

  const rr = sumTrack(rrDocs);
  const os = sumTrack(osDocs);
  const cam = sumTrack(camDocs);

  const remaining = status.critical_total - status.critical_verified;

  return (
    <div className="bg-honey-50 border-b border-honey-200 px-4 sm:px-8 py-3">
      <div className="max-w-[1280px] mx-auto flex items-center gap-6">
        {/* Deal name */}
        <h2 className="font-display text-lg font-semibold text-bark-800 whitespace-nowrap shrink-0">
          {dealName}
        </h2>

        {/* Track bars */}
        <div className="flex-1 flex items-center gap-4 min-w-0">
          {rr.total > 0 && (
            <TrackBar label="Rent Roll" verified={rr.verified} total={rr.total} />
          )}
          {os.total > 0 && (
            <TrackBar label="Operating Statement" verified={os.verified} total={os.total} />
          )}
          {cam.total > 0 && (
            <TrackBar label="CAM Rec" verified={cam.verified} total={cam.total} />
          )}
        </div>

        {/* Proceed button */}
        <div className="shrink-0 relative group">
          <button
            onClick={onProceed}
            disabled={!canProceed}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
              canProceed
                ? "bg-forest-600 text-white hover:bg-forest-700 shadow-sm"
                : "bg-grove-200 text-bark-400 cursor-not-allowed"
            }`}
          >
            Go to Cash Flow
          </button>
          {!canProceed && (
            <div className="absolute right-0 top-full mt-1 w-52 p-2 bg-bark-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              {remaining} critical field{remaining !== 1 ? "s" : ""} still need
              verification before proceeding to cash flow.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
